import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { MapPin } from "lucide-react";
import { FieldError, FieldHint, Label, Spinner, fieldErrorId } from "@/components/ui";
import { fieldControlClass, fieldPopoverClass } from "@/components/ui/field-variants";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { geocodingService } from "@/services/geocodingService";
import type { AddressSuggestion } from "@/types";

const DEBOUNCE_MS = 350;
const MIN_QUERY_LENGTH = 3;

export interface AddressAutocompleteInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onSelect: (suggestion: AddressSuggestion, sessionToken: string) => void;
  placeholder?: string;
  error?: string;
  /** Marks the field invalid (border + aria-invalid) without an inline message. */
  invalid?: boolean;
  /** Extra element ids merged into aria-describedby (e.g. external validation status). */
  describedBy?: string;
}

export function AddressAutocompleteInput({
  label,
  value,
  onChange,
  onSelect,
  placeholder,
  error,
  invalid = false,
  describedBy,
}: AddressAutocompleteInputProps) {
  const { t } = useTranslation("common");
  const inputId = useId();
  const listboxId = `${inputId}-suggestions`;

  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const requestIdRef = useRef(0);
  const sessionTokenRef = useRef<string | null>(null);
  /** Dropdown opens only after the user clicks the input (not focus-from-tab or hover). */
  const engagedByClickRef = useRef(false);
  /**
   * Text to look up. Set only by user interaction (typing or clicking the
   * field), so a prefilled address never triggers a paid geocoding call.
   */
  const [query, setQuery] = useState<string | null>(null);

  const ensureSessionToken = useCallback(() => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = geocodingService.createSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = null;
  }, []);

  const closeList = useCallback(() => {
    setIsOpen(false);
    setHighlightIndex(-1);
  }, []);

  const endEngagement = useCallback(() => {
    engagedByClickRef.current = false;
    closeList();
  }, [closeList]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        endEngagement();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [endEngagement]);

  const handleValueChange = (next: string) => {
    onChange(next);
    setQuery(next);
    if (next.trim().length < MIN_QUERY_LENGTH) {
      requestIdRef.current += 1;
      setSuggestions([]);
      setIsLoading(false);
      setFetchError(null);
      resetSessionToken();
      closeList();
    }
  };

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const trimmed = query?.trim() ?? "";
    if (trimmed.length < MIN_QUERY_LENGTH) {
      return;
    }

    debounceRef.current = setTimeout(() => {
      const requestId = ++requestIdRef.current;
      const sessionToken = ensureSessionToken();
      setIsLoading(true);
      setFetchError(null);
      void geocodingService
        .suggest(trimmed, sessionToken)
        .then((items) => {
          if (requestId !== requestIdRef.current) return;
          setSuggestions(items);
          if (engagedByClickRef.current) {
            setIsOpen(true);
          }
          setHighlightIndex(-1);
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) return;
          setSuggestions([]);
          setFetchError(t("addressAutocomplete.error"));
          setIsOpen(false);
        })
        .finally(() => {
          if (requestId === requestIdRef.current) {
            setIsLoading(false);
          }
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, closeList, ensureSessionToken, t]);

  const pickSuggestion = (suggestion: AddressSuggestion) => {
    const sessionToken = ensureSessionToken();
    onSelect(suggestion, sessionToken);
    resetSessionToken();
    setQuery(null);
    endEngagement();
  };

  const handleInputMouseDown = () => {
    engagedByClickRef.current = true;
    ensureSessionToken();
    if (value.trim().length >= MIN_QUERY_LENGTH) {
      if (query === null) {
        setQuery(value);
      }
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) {
      if (e.key === "Escape") closeList();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % suggestions.length);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) =>
        i <= 0 ? suggestions.length - 1 : i - 1,
      );
      return;
    }
    if (e.key === "Enter" && highlightIndex >= 0) {
      e.preventDefault();
      const picked = suggestions[highlightIndex];
      if (picked) pickSuggestion(picked);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      closeList();
    }
  };

  const queryLongEnough = value.trim().length >= MIN_QUERY_LENGTH;
  const showDropdown =
    isOpen &&
    queryLongEnough &&
    (isLoading || suggestions.length > 0 || !fetchError);

  const isInvalid = Boolean(error) || invalid;
  const ariaDescribedBy =
    [error ? fieldErrorId(inputId) : null, describedBy]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div ref={containerRef} className="relative w-full">
      {label && (
        <Label htmlFor={inputId} spacing="field">
          {label}
        </Label>
      )}
      <div className="relative">
        <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">
          <MapPin className="h-4 w-4" />
        </div>
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={showDropdown ? listboxId : undefined}
          aria-autocomplete="list"
          aria-invalid={isInvalid ? "true" : "false"}
          aria-describedby={ariaDescribedBy}
          autoComplete="off"
          value={value}
          placeholder={placeholder}
          className={fieldControlClass({
            className: "pl-10 pr-10",
          })}
          onChange={(e) => {
            const next = e.target.value;
            handleValueChange(next);
            if (
              engagedByClickRef.current &&
              next.trim().length >= MIN_QUERY_LENGTH
            ) {
              setIsOpen(true);
            }
          }}
          onMouseDown={handleInputMouseDown}
          onBlur={() => {
            engagedByClickRef.current = false;
          }}
          onKeyDown={handleKeyDown}
        />
        {isLoading && (
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-surface-400">
            <Spinner
              size="sm"
              variant="muted"
              loadingLabel={t("loading")}
            />
          </div>
        )}
      </div>

      {showDropdown && (
        <ul
          id={listboxId}
          role="listbox"
          className={fieldPopoverClass(
            "absolute z-50 mt-1 max-h-60 w-full overflow-auto py-1",
          )}
        >
          {isLoading && suggestions.length === 0 ? (
            <li className="flex items-center justify-center gap-2 px-4 py-2.5">
              <Spinner size="sm" variant="muted" loadingLabel={t("loading")} />
              <span className="text-sm text-surface-500">
                {t("addressAutocomplete.loading")}
              </span>
            </li>
          ) : suggestions.length === 0 ? (
            <li className="px-4 py-2.5 text-sm text-surface-500">
              {t("addressAutocomplete.noStreetResults")}
            </li>
          ) : (
            suggestions.map((suggestion, index) => (
              <li
                key={suggestion.id}
                role="option"
                aria-selected={index === highlightIndex}
                className={cn(
                  "cursor-pointer px-4 py-2.5 text-sm text-surface-900",
                  index === highlightIndex && "bg-primary-50",
                  "hover:bg-surface-50",
                )}
                onMouseEnter={() => setHighlightIndex(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickSuggestion(suggestion)}
              >
                {suggestion.label}
              </li>
            ))
          )}
        </ul>
      )}

      {error && (
        <FieldError id={fieldErrorId(inputId)}>{error}</FieldError>
      )}
      {fetchError && !error && (
        <FieldHint>{fetchError}</FieldHint>
      )}
    </div>
  );
}
